import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';

const ExclusiveOffers = () => {
  const { t } = useTranslation();
  const { formatPrice, language } = useLanguageCurrency();
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState({ days: 2, hours: 12, minutes: 43, seconds: 59 });
  
  // Same structure, 6 slides – content shown as Coming Soon (phase)
  const exclusiveOffers = useMemo(() =>
    [1, 2, 3, 4, 5, 6].map((i) => ({
      id: i,
      comingSoon: true,
    })),
    []
  );

  // Update countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        let { days, hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else if (days > 0) {
          days--;
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSlider = () => {
    const slider = document.getElementById('deals-slider');
    if (slider) {
      slider.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const nextOffer = () => {
    if (exclusiveOffers.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % exclusiveOffers.length);
  };

  const prevOffer = () => {
    if (exclusiveOffers.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + exclusiveOffers.length) % exclusiveOffers.length);
  };

  const currentOffer = exclusiveOffers[currentIndex];
  const isComingSoon = currentOffer?.comingSoon === true;

  return (
    <div className="min-h-screen overflow-hidden" style={{ backgroundColor: '#1D2A3B', width: '100%', position: 'static' }}>
      <Navbar />

      {/* Urgency Strip - Sticky Bar - Hidden on Mobile */}
      <div className="hidden sm:block sticky top-0 z-50 w-full py-3 px-4 bg-gradient-to-r from-[#D5B577] via-[#c4a366] to-[#D5B577] shadow-lg" style={{ borderBottom: '2px solid rgba(29, 42, 59, 0.3)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#1D2A3B' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-bold text-sm sm:text-base" style={{ color: '#1D2A3B' }}>
              {exclusiveOffers.length} {language === 'ar' ? 'عروض تنتهي خلال' : 'Offers ending in'} {timeRemaining.days}d {timeRemaining.hours}h {timeRemaining.minutes}m
            </span>
          </div>
          <button
            onClick={scrollToSlider}
            className="px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 hover:scale-105"
            style={{ 
              backgroundColor: '#1D2A3B', 
              color: '#D5B577',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}
          >
            {language === 'ar' ? 'عرض العروض' : 'View Deals'}
          </button>
        </div>
      </div>

      {/* Cinematic Hero Section */}
      <section className="relative h-[107.5vh] flex items-center justify-center overflow-hidden">
        {/* Parallax Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1613977257363-707ba9348227?w=1920&h=1080&fit=crop)',
            transform: 'scale(1.1)',
            transition: 'transform 0.3s ease-out'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>
          {/* Bottom fade to blend with next section */}
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-b from-transparent via-black/40 to-black/80"></div>
        </div>

        {/* Gold Particle Animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-float-particle"
              style={{
                width: `${Math.random() * 4 + 2}px`,
                height: `${Math.random() * 4 + 2}px`,
                backgroundColor: '#D5B577',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.5 + 0.3,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 3 + 2}s`,
                boxShadow: '0 0 10px rgba(213, 181, 119, 0.8)'
              }}
            ></div>
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" style={{ transform: 'translateY(-15%)' }}>
          <div className="mb-6">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ 
              backgroundColor: 'rgba(213, 181, 119, 0.2)', 
              color: '#D5B577',
              border: '1px solid rgba(213, 181, 119, 0.4)',
              backdropFilter: 'blur(10px)'
            }}>
              ✨ {language === 'ar' ? 'عروض فاخرة محدودة' : 'Limited Luxury Offers'}
            </span>
          </div>
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight" style={{
            color: '#FFFFFF',
            textShadow: '0 4px 20px rgba(0, 0, 0, 0.5), 0 0 40px rgba(213, 181, 119, 0.3)'
          }}>
            {language === 'ar' 
              ? 'فيلات ومنتجعات مختارة يدوياً'
              : 'Hand-picked appartments & resorts'}
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl mb-8 text-gray-200" style={{ textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)' }}>
            {language === 'ar' 
              ? 'وفر حتى 35%'
              : 'Save up to 35%'}
          </p>
          <button
            onClick={scrollToSlider}
            className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, #D5B577 0%, #c4a366 100%)',
              color: '#1D2A3B',
              boxShadow: '0 8px 24px rgba(213, 181, 119, 0.4)',
            }}
          >
            {language === 'ar' ? 'استكشف العروض' : 'Explore Offers'}
          </button>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Horizontal Deal Slider Section */}
      <section id="deals-slider" className="pt-12 pb-20 px-4 sm:px-6 lg:px-8 relative" style={{ 
        backgroundColor: '#1D2A3B', 
        minHeight: '100vh',
        marginTop: '-120px',
        zIndex: 10,
        paddingTop: '140px'
      }}>
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
              {language === 'ar' ? 'عروض حصرية' : 'Exclusive Deals'}
            </h2>
            <p className="text-lg text-gray-300 mb-6">
              {language === 'ar' ? 'كل عرض فريد من نوعه' : 'Each deal is uniquely exclusive'}
            </p>
            
            {/* Countdown Timer */}
            <div 
              className="inline-flex items-center gap-3 px-6 py-3 rounded-xl"
              style={{
                background: 'rgba(213, 181, 119, 0.05)',
                border: '1px solid rgba(213, 181, 119, 0.2)',
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gray-300 text-sm">
                {language === 'ar' ? 'تنتهي خلال:' : 'Ends in:'}
              </span>
              <span className="text-xl font-bold" style={{ color: '#D5B577' }}>
                {String(timeRemaining.days).padStart(2, '0')}d {String(timeRemaining.hours).padStart(2, '0')}h {String(timeRemaining.minutes).padStart(2, '0')}m
              </span>
            </div>
          </div>

          {/* Slider Container - same design, cards show as Coming Soon */}
          <div className="relative">
            {/* Navigation Arrows */}
            <button
              onClick={prevOffer}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              style={{
                backgroundColor: 'rgba(213, 181, 119, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(213, 181, 119, 0.4)',
                color: '#D5B577'
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextOffer}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              style={{
                backgroundColor: 'rgba(213, 181, 119, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(213, 181, 119, 0.4)',
                color: '#D5B577'
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Deal Card - same structure, Coming Soon content */}
            <div 
              className="rounded-3xl overflow-hidden transition-all duration-700"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(213, 181, 119, 0.3)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(20px)',
                ...(isComingSoon && { cursor: 'default', pointerEvents: 'none' }),
              }}
            >
              <div className="flex flex-col lg:flex-row">
                {/* Left: Big Image Area - same size */}
                <div className="w-full lg:w-1/2 relative h-96 lg:h-[600px] overflow-hidden flex items-center justify-center" style={isComingSoon ? { background: 'linear-gradient(135deg, rgba(29, 42, 59, 0.98) 0%, rgba(45, 55, 75, 0.95) 50%, rgba(29, 42, 59, 0.98) 100%)' } : {}}>
                  {!isComingSoon ? (
                    <>
                      <img
                        src={currentOffer.image}
                        alt={currentOffer.title}
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute top-6 right-6 px-6 py-3 rounded-xl font-bold text-xl shadow-2xl" style={{ background: 'linear-gradient(135deg, #D5B577 0%, #c4a366 100%)', color: '#1D2A3B', boxShadow: '0 8px 24px rgba(213, 181, 119, 0.5)' }}>
                        {currentOffer.discount}% {language === 'ar' ? 'خصم' : 'OFF'}
                      </div>
                    </>
                  ) : (
                    <>
                      <svg className="w-24 h-24 opacity-40" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13M5 12h14M12 5v7M5 12a2 2 0 110-4h14a2 2 0 110 4M12 12a2 2 0 110-4" />
                      </svg>
                      <div className="absolute top-6 right-6 px-6 py-3 rounded-xl font-bold text-xl shadow-2xl" style={{ background: 'linear-gradient(135deg, #D5B577 0%, #c4a366 100%)', color: '#1D2A3B', boxShadow: '0 8px 24px rgba(213, 181, 119, 0.5)' }}>
                        {t('properties.comingSoon')}
                      </div>
                    </>
                  )}
                </div>

                {/* Right: Property Details - same layout */}
                <div className="w-full lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center" style={{ backgroundColor: 'rgba(29, 42, 59, 0.8)' }}>
                  <div className="mb-4">
                    <span className="text-sm font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: 'rgba(213, 181, 119, 0.2)', color: '#D5B577' }}>
                      {language === 'ar' ? 'حصري' : 'EXCLUSIVE'}
                    </span>
                  </div>

                  <h3 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
                    {isComingSoon ? t('properties.comingSoon') : currentOffer.title}
                  </h3>

                  <div className="flex items-center gap-2 mb-6">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-lg text-gray-300">{isComingSoon ? '—' : currentOffer.location}</span>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-2 mb-6">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5" fill="none" stroke="#D5B577" viewBox="0 0 24 24" style={{ opacity: isComingSoon ? 0.3 : 1 }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      ))}
                    </div>
                    <span className="text-lg font-semibold" style={{ color: '#D5B577' }}>{isComingSoon ? '—' : currentOffer.rating}</span>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="text-4xl md:text-5xl font-bold" style={{ color: '#D5B577' }}>
                        {isComingSoon ? '—' : formatPrice(currentOffer.price, false)}
                      </span>
                      {!isComingSoon && (
                        <span className="text-xl line-through text-gray-500">
                          {formatPrice(currentOffer.originalPrice, false)}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-400">{t('exclusiveOffers.perNight')}</span>
                  </div>

                  {/* Amenities */}
                  <div className="mb-8">
                    <p className="text-sm font-semibold mb-3" style={{ color: '#D5B577' }}>
                      {language === 'ar' ? 'المرافق:' : 'Amenities:'}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {isComingSoon ? (
                        <span className="px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: 'rgba(213, 181, 119, 0.1)', color: 'rgba(213, 181, 119, 0.6)', border: '1px solid rgba(213, 181, 119, 0.3)' }}>—</span>
                      ) : (
                        currentOffer.amenities.map((amenity, idx) => (
                          <span key={idx} className="px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: 'rgba(213, 181, 119, 0.1)', color: '#D5B577', border: '1px solid rgba(213, 181, 119, 0.3)' }}>
                            {amenity}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={!isComingSoon ? () => navigate(`/properties/${currentOffer.id}`) : undefined}
                    disabled={isComingSoon}
                    className="w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105"
                    style={{
                      background: isComingSoon ? 'rgba(213, 181, 119, 0.2)' : 'linear-gradient(135deg, #D5B577 0%, #c4a366 100%)',
                      color: '#1D2A3B',
                      boxShadow: isComingSoon ? 'none' : '0 8px 24px rgba(213, 181, 119, 0.4)',
                      cursor: isComingSoon ? 'default' : 'pointer',
                    }}
                  >
                    {isComingSoon ? t('properties.comingSoon') : (language === 'ar' ? 'احجز الآن' : 'Book Now')}
                  </button>
                </div>
              </div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {exclusiveOffers.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex ? 'w-8' : ''
                  }`}
                  style={{
                    backgroundColor: index === currentIndex ? '#D5B577' : 'rgba(213, 181, 119, 0.3)'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* CSS Animations */}
      <style>{`
        @keyframes float-particle {
          0%, 100% {
            transform: translateY(0) translateX(0) rotate(0deg);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-20px) translateX(10px) rotate(180deg);
            opacity: 1;
          }
        }

        @keyframes pulse-glow {
          0%, 100% {
            opacity: 0.2;
            transform: scale(1);
          }
          50% {
            opacity: 0.4;
            transform: scale(1.1);
          }
        }

        .animate-float-particle {
          animation: float-particle 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ExclusiveOffers;
